import type { QuoteInput, Market } from '@quote-tool/shared';

export interface CatalogMeta {
  market: Market;
  lastRefreshedAt: string | null;
  refreshInProgress: boolean;
  lastError: string | null;
  productsCount: number;
  uniqueOptionsCount: number;
  productsByFamily: Record<string, number>;
}

export interface PriceChange {
  kind: 'product' | 'option';
  key: string;
  optionType?: string;
  before: number | null;
  after: number;
}

export interface MarketRefreshResult {
  market: Market;
  refreshedAt: string;
  baremetal: {
    productsInserted: number;
    optionsInserted: number;
    skippedPlans: number;
    skippedAddons: number;
  };
  hpcVmware: {
    productsInserted: number;
    optionsInserted: number;
    productsByCategory: Record<string, number>;
    hostsSkippedZeroPrice: number;
    hostsSkippedNoMatchingPricing: number;
  };
  priceDiff: {
    productsChanged: number;
    optionsChanged: number;
    samples: PriceChange[];
  };
}

export interface RefreshResult {
  ok: true;
  refreshedAt: string;
  baremetal: MarketRefreshResult['baremetal'];
  hpcVmware: MarketRefreshResult['hpcVmware'];
  /** One entry per market refreshed (CA and WORLD when no market is requested). */
  markets: MarketRefreshResult[];
  /** Markets that failed; the others still refreshed. */
  failures: Array<{ market: Market; error: string }>;
}

async function jsonFetch<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await fetch(path, init);
  const body = await res.json().catch(() => ({}));
  if (!res.ok) {
    const message = (body as { error?: string }).error ?? `HTTP ${res.status}`;
    throw new Error(message);
  }
  return body as T;
}

export interface CatalogOption {
  optionCode: string;
  type: string;
  // addonFamily name, e.g. 'storage' (data array) vs 'system-storage' (included
  // system disk) — both have type 'disk'. null when the payload had no mapping.
  family: string | null;
  name: string;
  monthlyPriceCad: number;
  setupFeeCad: number;
  appliesCommitmentDiscount: boolean;
}

export interface CatalogProduct {
  id: number;
  planCode: string;
  family: string;          // 'baremetal' | 'hpc_vmware'
  range: string;            // 'advance' | 'scale' | 'high-grade' | 'game' | 'hpc-vsphere' | 'hpc-hcs' | 'hpc-nsx' | 'hpc-sddc'
  name: string;
  descriptionEn: string | null;
  descriptionFr: string | null;
  monthlyPriceCad: number;
  setupFeeCad: number;
  isEco: boolean;
  servicePackCode: string | null;
  hasVsan: boolean;
  datacenters: string[];
  releaseYear: number | null;
  options: CatalogOption[];
}

export interface CatalogPayload {
  lastRefreshedAt: string | null;
  products: CatalogProduct[];
}

export const api = {
  // Market-scoped status banner. Without a market the backend answers for CA.
  getCatalogMeta(market?: Market): Promise<CatalogMeta> {
    return jsonFetch<CatalogMeta>(
      '/api/catalog/meta' + (market === 'WORLD' ? '?market=WORLD' : ''),
    );
  },
  // No market argument = refresh EVERY market (CA + WORLD), so a price change at
  // OVH lands in both catalog.db and catalog-world.db from one click.
  refreshCatalog(market?: Market): Promise<RefreshResult> {
    const qs = market ? `?market=${market}` : '';
    return jsonFetch<RefreshResult>('/api/catalog/refresh' + qs, { method: 'POST' });
  },
  // Market-scoped catalog. CA (default / no arg) hits '/api/catalog' unchanged;
  // WORLD appends '?market=WORLD' so the backend opens catalog-world.db.
  getCatalog(market?: Market): Promise<CatalogPayload> {
    return jsonFetch<CatalogPayload>(
      '/api/catalog' + (market === 'WORLD' ? '?market=WORLD' : ''),
    );
  },
  // Generate the quote PDF. Returns the raw PDF bytes as a Blob — does NOT use
  // jsonFetch (which always parses JSON); the response is application/pdf.
  //
  // `reuseQuoteNumber` reprints a saved deal under its original number instead
  // of minting a new one. The number comes back in a header so the caller can
  // show it (and later reprint) without parsing the filename.
  async generateQuotePdf(
    quote: QuoteInput,
    reuseQuoteNumber?: string,
  ): Promise<{ blob: Blob; quoteNumber: string | null }> {
    const res = await fetch('/api/quote/pdf', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(reuseQuoteNumber ? { ...quote, quoteNumber: reuseQuoteNumber } : quote),
    });
    if (!res.ok) {
      const msg = await res.json().then((b) => b.error).catch(() => `HTTP ${res.status}`);
      throw new Error(msg);
    }
    return { blob: await res.blob(), quoteNumber: res.headers.get('X-Quote-Number') };
  },
};

// ---- Session, clients and deals (logged-in features) -----------------------

export interface SessionUser {
  id: string;
  username: string;
  email: string | null;
  fullName: string | null;
  isSuperuser: boolean;
  isUsersAdmin: boolean;
  isPresales: boolean;
  isActive: boolean;
  mustChangePassword: boolean;
  lastLoginAt: string | null;
}

/** Superuser, or delegated account administration. */
export function canManageUsers(u: SessionUser | null): boolean {
  return !!u && (u.isSuperuser || u.isUsersAdmin);
}

/**
 * Who may hand out the presales flag. Mirrors canGrantPresales on the server,
 * which is where it is actually enforced — this only decides whether to render
 * the checkbox.
 */
export function canGrantPresales(u: SessionUser | null): boolean {
  if (!u) return false;
  return u.isSuperuser || (u.isPresales && u.isUsersAdmin);
}

export interface UserPage {
  users: SessionUser[];
  total: number;
  page: number;
  pageSize: number;
  pageCount: number;
}

export interface UserPatch {
  fullName?: string;
  email?: string;
  isUsersAdmin?: boolean;
  isPresales?: boolean;
  isSuperuser?: boolean;
  isActive?: boolean;
  password?: string;
}

export const usersApi = {
  list(page: number, search: string, pageSize = 20): Promise<UserPage> {
    const qs = new URLSearchParams({ page: String(page), pageSize: String(pageSize) });
    if (search.trim()) qs.set('q', search.trim());
    return jsonFetch<UserPage>(`/api/users?${qs.toString()}`);
  },
  create(body: UserPatch & { username: string; password: string }): Promise<{ user: SessionUser }> {
    return jsonFetch<{ user: SessionUser }>('/api/users', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    });
  },
  update(id: string, patch: UserPatch): Promise<{ user: SessionUser }> {
    return jsonFetch<{ user: SessionUser }>(`/api/users/${encodeURIComponent(id)}`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(patch),
    });
  },
};

/** Mirrors ClientInfo, plus the stored id. */
export interface SavedClient {
  id: string;
  company: string;
  attention: string;
  contactName: string;
  contactEmail: string;
  contactPhone: string;
  address: string;
  city: string;
  province?: string;
  country?: string;
  postalCode: string;
  nic: string;
  taxExempt: boolean;
}

export interface DealSummary {
  id: string;
  clientId: string;
  market: string | null;
  language: string | null;
  repName: string | null;
  totalMonthly: number | null;
  totalSetup: number | null;
  lineCount: number | null;
  /** null = unowned legacy deal: visible to everyone, and claimable. */
  ownerId: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface ClientWithDeals {
  client: SavedClient;
  deals: DealSummary[];
}

export const authApi = {
  /** Who is logged in. Returns null for anonymous — never throws on 401. */
  me(): Promise<{ user: SessionUser | null }> {
    return jsonFetch<{ user: SessionUser | null }>('/api/me');
  },
  login(username: string, password: string): Promise<{ user: SessionUser }> {
    return jsonFetch<{ user: SessionUser }>('/api/login', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ username, password }),
    });
  },
  logout(): Promise<{ ok: true }> {
    return jsonFetch<{ ok: true }>('/api/logout', { method: 'POST' });
  },
};

export const crmApi = {
  /** Matching saved clients. The backend returns [] below its minimum length. */
  searchClients(q: string): Promise<{ clients: SavedClient[]; minChars: number }> {
    return jsonFetch<{ clients: SavedClient[]; minChars: number }>(
      `/api/clients/search?q=${encodeURIComponent(q)}`,
    );
  },
  /** Only the caller's own deals, plus unowned ones. Scoped on the server. */
  listDeals(filter?: string): Promise<{ clients: ClientWithDeals[] }> {
    const qs = filter ? `?filter=${encodeURIComponent(filter)}` : '';
    return jsonFetch<{ clients: ClientWithDeals[] }>(`/api/deals${qs}`);
  },
  getDeal(id: string): Promise<{ deal: DealSummary & { quote: QuoteInput; client: SavedClient } }> {
    return jsonFetch(`/api/deals/${encodeURIComponent(id)}`);
  },
  deleteDeal(id: string): Promise<{ ok: true }> {
    return jsonFetch(`/api/deals/${encodeURIComponent(id)}`, { method: 'DELETE' });
  },
  /** Claim an unowned deal. 409 if another rep got there first. */
  claimDeal(id: string): Promise<{ ok: true }> {
    return jsonFetch(`/api/deals/${encodeURIComponent(id)}/claim`, { method: 'POST' });
  },
};
